<template>
  <div class="bshe-subtab">

    <!-- ─── Section: Jours fériés ─── -->
    <div class="bshe-section">
      <div class="bshe-section-header" @click="toggleSection('holidays')">
        <h2 class="bshe-section-title">
          {{ $t('dash.presenceManagement.specialHours.holidays.title') }} {{ currentYear }}
        </h2>
        <i class="material-icons bshe-section-chevron">
          {{ sectionsOpen.holidays ? 'expand_less' : 'expand_more' }}
        </i>
      </div>

      <div v-show="sectionsOpen.holidays" class="bshe-section-content">
        <div class="bshe-cards-grid">

          <div
            v-for="holiday in holidays"
            :key="holiday.key"
            class="bshe-card"
            :class="{ 'bshe-card--expanded': holiday.isExpanded }"
          >
            <div class="bshe-card-header" @click="toggleHoliday(holiday.key)">
              <div class="bshe-card-header-left">
                <p class="bshe-card-name">
                  {{ holiday.isCustom ? formatDate(holiday.date) : holiday.name }}
                </p>
                <p v-if="!holiday.isCustom" class="bshe-card-date">
                  {{ formatDate(holiday.date) }}
                </p>
                <p v-else class="bshe-card-date bshe-card-date--custom">
                  {{ $t('dash.presenceManagement.specialHours.customDayCreated') }}
                </p>
              </div>
              <div class="bshe-card-header-right">
                <button
                  v-if="holiday.isCustom"
                  class="bshe-icon-btn bshe-icon-btn--del bshe-card-del-btn"
                  @click.stop="removeCustomDay(holiday.key)"
                >
                  <i class="material-icons">delete</i>
                </button>
                <i class="material-icons bshe-card-chevron">
                  {{ holiday.isExpanded ? 'expand_less' : 'expand_more' }}
                </i>
              </div>
            </div>

            <div v-if="holiday.isExpanded" class="bshe-card-editor">
              <div class="bshe-toggle-group">
                <vs-switch v-model="holiday.isOpen" color="#6a1f4c" @input="onToggleHolidayOpen(holiday.key, $event)" />
                <span class="bshe-toggle-label">
                  {{ holiday.isOpen ? $t('dash.presenceManagement.hours.open') : $t('dash.presenceManagement.hours.closed') }}
                </span>
              </div>

              <div class="bshe-toggle-group">
                <vs-switch v-model="holiday.isAllDay" color="#6a1f4c" :disabled="!holiday.isOpen" />
                <span class="bshe-toggle-label bshe-toggle-label--secondary">
                  {{ $t('dash.presenceManagement.hours.allDay') }}
                </span>
              </div>

              <template v-if="holiday.isOpen && !holiday.isAllDay">
                <div v-for="(slot, idx) in holiday.slots" :key="idx" class="bshe-slot-row">
                  <input type="time" v-model="slot.from" class="bshe-time-input" />
                  <span class="bshe-time-sep">{{ $t('dash.presenceManagement.hours.to') }}</span>
                  <input type="time" v-model="slot.to" class="bshe-time-input" />
                  <span class="bshe-slot-actions">
                    <button v-if="idx > 0" class="bshe-icon-btn bshe-icon-btn--del" @click.stop="removeHolidaySlot(holiday.key, idx)">
                      <i class="material-icons">delete</i>
                    </button>
                    <button v-if="idx === 0 && holiday.slots.length < 4" class="bshe-icon-btn bshe-icon-btn--add" @click.stop="addHolidaySlot(holiday.key)">
                      <i class="material-icons">add_circle_outline</i>
                    </button>
                  </span>
                </div>
              </template>
            </div>
          </div>

          <div class="bshe-card bshe-card--add" @click="openAddCustomDayModal">
            <p class="bshe-card-add-label">{{ $t('dash.presenceManagement.specialHours.addCustomDay') }}</p>
            <i class="material-icons bshe-card-add-icon">add_circle_outline</i>
          </div>
        </div>
      </div>
    </div>

    <!-- ─── Section: Vacances ─── -->
    <div class="bshe-section">
      <div class="bshe-section-header" @click="toggleSection('vacations')">
        <h2 class="bshe-section-title">
          {{ $t('dash.presenceManagement.specialHours.vacations.title') }} {{ currentYear }}
        </h2>
        <i class="material-icons bshe-section-chevron">
          {{ sectionsOpen.vacations ? 'expand_less' : 'expand_more' }}
        </i>
      </div>

      <div v-show="sectionsOpen.vacations" class="bshe-section-content bshe-section-content--vacations">
        <p class="bshe-vacations-hint">{{ $t('dash.presenceManagement.specialHours.vacations.hint') }}</p>

        <div v-for="(period, idx) in vacationPeriods" :key="period.id" class="bshe-vacation-row">
          <!-- <input type="text" v-model="period.name" class="bshe-text-input" :placeholder="$t('dash.presenceManagement.specialHours.vacations.namePlaceholder')" /> -->
          <span class="bshe-vac-sep">{{ $t('dash.presenceManagement.specialHours.vacations.from') }}</span>
          <input type="date" v-model="period.startDate" class="bshe-date-input" />
          <span class="bshe-vac-sep">{{ $t('dash.presenceManagement.specialHours.vacations.to') }}</span>
          <input type="date" v-model="period.endDate" class="bshe-date-input" />
          <button class="bshe-icon-btn bshe-icon-btn--del" @click="removeVacationPeriod(idx)">
            <i class="material-icons">delete</i>
          </button>
        </div>

        <button class="bshe-add-vacation-btn" @click="addVacationPeriod">
          <i class="material-icons">add_circle_outline</i>
          <span>{{ $t('dash.presenceManagement.specialHours.vacations.add') }}</span>
        </button>
      </div>
    </div>

    <!-- ─── Section: Archives (Jours spéciaux passés) ─── -->
    <div class="bshe-section" v-if="pastSpecialHours && pastSpecialHours.length">
      <div class="bshe-section-header" @click="toggleSection('archives')">
        <h2 class="bshe-section-title">
          {{ $t('dash.presenceManagement.specialHours.archives.title') }} ({{ pastSpecialHours.length }})
        </h2>
        <i class="material-icons bshe-section-chevron">
          {{ sectionsOpen.archives ? 'expand_less' : 'expand_more' }}
        </i>
      </div>

      <div v-show="sectionsOpen.archives" class="bshe-section-content">
        <div class="bshe-archives-toolbar">
          <p class="bshe-vacations-hint">
            {{ $t('dash.presenceManagement.specialHours.archives.hint') }}
          </p>
          <div class="bshe-archives-actions">
            <button type="button" class="bshe-btn-archive-action bshe-btn-archive-action--renew" @click="renewAllArchives">
              <i class="material-icons">autorenew</i>
              <span>{{ $t('dash.presenceManagement.specialHours.archives.renewAll') }}</span>
            </button>
            <button type="button" class="bshe-btn-archive-action bshe-btn-archive-action--purge" @click="purgeAllArchives">
              <i class="material-icons">delete_sweep</i>
              <span>{{ $t('dash.presenceManagement.specialHours.archives.purgeAll') }}</span>
            </button>
          </div>
        </div>

        <div class="bshe-archives-list">
          <div v-for="(item, idx) in pastSpecialHours" :key="item.id" class="bshe-archive-card">
            <div class="bshe-archive-info">
              <span class="bshe-archive-date">{{ formatDate(item.date) }}</span>
              <span class="bshe-archive-status" :class="item.isOpen ? 'bshe-archive-status--open' : 'bshe-archive-status--closed'">
                {{ item.isOpen ? (item.openTime + ' - ' + item.closeTime) : $t('dash.presenceManagement.hours.closed') }}
              </span>
            </div>
            <div class="bshe-archive-item-actions">
              <button 
                type="button" 
                class="bshe-btn-renew-item" 
                @click="renewPastItem(idx)"
                :title="$t('dash.presenceManagement.specialHours.archives.renewTooltip')"
              >
                <i class="material-icons">autorenew</i>
                <span>{{ $t('dash.presenceManagement.specialHours.archives.renew') }}</span>
              </button>
              <button 
                type="button" 
                class="bshe-icon-btn bshe-icon-btn--del" 
                @click="removePastItem(idx)"
                :title="$t('dash.presenceManagement.specialHours.archives.deleteTooltip')"
              >
                <i class="material-icons">delete</i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ─── Modal: Add custom day ─── -->
    <div v-if="showAddCustomDayModal" class="bshe-modal-overlay" @click.self="showAddCustomDayModal = false">
      <div class="bshe-modal">
        <h3 class="bshe-modal-title">{{ $t('dash.presenceManagement.specialHours.customDayModal.title') }}</h3>

        <!-- <div class="bshe-modal-field">
          <label class="bshe-modal-label">{{ $t('dash.presenceManagement.specialHours.customDayModal.nameLabel') }}</label>
          <input type="text" v-model="customDayForm.name" class="bshe-text-input" :placeholder="$t('dash.presenceManagement.specialHours.customDayModal.namePlaceholder')" />
        </div> -->

        <div class="bshe-modal-field">
          <label class="bshe-modal-label">{{ $t('dash.presenceManagement.specialHours.customDayModal.dateLabel') }}</label>
          <input type="date" v-model="customDayForm.date" class="bshe-date-input" />
        </div>

        <div class="bshe-modal-actions">
          <vs-button color="#6a1f4c" type="border" @click="showAddCustomDayModal = false">
            {{ $t('dash.presenceManagement.hours.confirm.cancel') }}
          </vs-button>
          <vs-button color="#6a1f4c" type="filled" :disabled="!customDayForm.date" @click="addCustomDay">
            {{ $t('dash.presenceManagement.specialHours.customDayModal.confirm') }}
          </vs-button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
function computeEaster(year) {
  const a = year % 19, b = Math.floor(year / 100), c = year % 100
  const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25)
  const g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const month = Math.floor((h + l - 7 * m + 114) / 31)
  const day = ((h + l - 7 * m + 114) % 31) + 1
  return new Date(year, month - 1, day)
}

function addDays(date, days) {
  const r = new Date(date); r.setDate(r.getDate() + days); return r
}

function toDateStr(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

function getHolidaysForLocale(locale, year) {
  const easter = computeEaster(year)
  
  // Friday before Easter (Good Friday)
  const goodFridayStr = toDateStr(addDays(easter, -2))
  // Monday after Easter (Easter Monday)
  const easterMondayStr = toDateStr(addDays(easter, 1))
  // Ascension (Easter + 39 days)
  const ascensionStr = toDateStr(addDays(easter, 39))
  // Whit Monday (Easter + 50 days)
  const whitMondayStr = toDateStr(addDays(easter, 50))
  // Corpus Christi (Easter + 60 days)
  const corpusChristiStr = toDateStr(addDays(easter, 60))

  // Find day of week helper for UK Bank Holidays
  const getNthWeekdayInMonth = (year, month, dayOfWeek, n) => {
    if (n > 0) {
      let date = new Date(year, month, 1)
      while (date.getDay() !== dayOfWeek) {
        date.setDate(date.getDate() + 1)
      }
      date.setDate(date.getDate() + (n - 1) * 7)
      return date
    } else {
      let date = new Date(year, month + 1, 0)
      while (date.getDay() !== dayOfWeek) {
        date.setDate(date.getDate() - 1)
      }
      return date
    }
  }

  const holidays = []
  
  switch (locale.toLowerCase()) {
    case 'fr':
      holidays.push(
        { key: `new-year-${year}`, nameKey: 'newYear', date: `${year}-01-01` },
        { key: `easter-monday-${year}`, nameKey: 'easterMonday', date: easterMondayStr },
        { key: `labour-day-${year}`, nameKey: 'labourDay', date: `${year}-05-01` },
        { key: `victory-day-${year}`, nameKey: 'victoryDay', date: `${year}-05-08` },
        { key: `ascension-${year}`, nameKey: 'ascension', date: ascensionStr },
        { key: `whit-monday-${year}`, nameKey: 'whitMonday', date: whitMondayStr },
        { key: `bastille-day-${year}`, nameKey: 'bastilleDay', date: `${year}-07-14` },
        { key: `assumption-${year}`, nameKey: 'assumptionDay', date: `${year}-08-15` },
        { key: `all-saints-${year}`, nameKey: 'allSaints', date: `${year}-11-01` },
        { key: `armistice-${year}`, nameKey: 'armistice', date: `${year}-11-11` },
        { key: `christmas-${year}`, nameKey: 'christmas', date: `${year}-12-25` }
      )
      break
    case 'en':
      holidays.push(
        { key: `new-year-${year}`, nameKey: 'newYear', date: `${year}-01-01` },
        { key: `good-friday-${year}`, nameKey: 'goodFriday', date: goodFridayStr },
        { key: `easter-monday-${year}`, nameKey: 'easterMonday', date: easterMondayStr },
        { key: `early-may-${year}`, nameKey: 'earlyMayBankHoliday', date: toDateStr(getNthWeekdayInMonth(year, 4, 1, 1)) },
        { key: `spring-bank-${year}`, nameKey: 'springBankHoliday', date: toDateStr(getNthWeekdayInMonth(year, 4, 1, -1)) },
        { key: `summer-bank-${year}`, nameKey: 'summerBankHoliday', date: toDateStr(getNthWeekdayInMonth(year, 7, 1, -1)) },
        { key: `christmas-${year}`, nameKey: 'christmas', date: `${year}-12-25` },
        { key: `boxing-day-${year}`, nameKey: 'boxingDay', date: `${year}-12-26` }
      )
      break
    case 'es':
      holidays.push(
        { key: `new-year-${year}`, nameKey: 'newYear', date: `${year}-01-01` },
        { key: `epiphany-${year}`, nameKey: 'epiphany', date: `${year}-01-06` },
        { key: `good-friday-${year}`, nameKey: 'goodFriday', date: goodFridayStr },
        { key: `labour-day-${year}`, nameKey: 'labourDay', date: `${year}-05-01` },
        { key: `assumption-${year}`, nameKey: 'assumptionDay', date: `${year}-08-15` },
        { key: `national-day-${year}`, nameKey: 'nationalDay', date: `${year}-10-12` },
        { key: `all-saints-${year}`, nameKey: 'allSaints', date: `${year}-11-01` },
        { key: `constitution-day-${year}`, nameKey: 'constitutionDay', date: `${year}-12-06` },
        { key: `immaculate-${year}`, nameKey: 'immaculateConception', date: `${year}-12-08` },
        { key: `christmas-${year}`, nameKey: 'christmas', date: `${year}-12-25` }
      )
      break
    case 'de':
      holidays.push(
        { key: `new-year-${year}`, nameKey: 'newYear', date: `${year}-01-01` },
        { key: `good-friday-${year}`, nameKey: 'goodFriday', date: goodFridayStr },
        { key: `easter-monday-${year}`, nameKey: 'easterMonday', date: easterMondayStr },
        { key: `labour-day-${year}`, nameKey: 'labourDay', date: `${year}-05-01` },
        { key: `ascension-${year}`, nameKey: 'ascension', date: ascensionStr },
        { key: `whit-monday-${year}`, nameKey: 'whitMonday', date: whitMondayStr },
        { key: `german-unity-${year}`, nameKey: 'germanUnity', date: `${year}-10-03` },
        { key: `christmas-${year}`, nameKey: 'christmas', date: `${year}-12-25` },
        { key: `boxing-day-${year}`, nameKey: 'boxingDay', date: `${year}-12-26` }
      )
      break
    case 'it':
      holidays.push(
        { key: `new-year-${year}`, nameKey: 'newYear', date: `${year}-01-01` },
        { key: `epiphany-${year}`, nameKey: 'epiphany', date: `${year}-01-06` },
        { key: `easter-monday-${year}`, nameKey: 'easterMonday', date: easterMondayStr },
        { key: `liberation-${year}`, nameKey: 'liberationDay', date: `${year}-04-25` },
        { key: `labour-day-${year}`, nameKey: 'labourDay', date: `${year}-05-01` },
        { key: `republic-day-${year}`, nameKey: 'republicDay', date: `${year}-06-02` },
        { key: `assumption-${year}`, nameKey: 'assumptionDay', date: `${year}-08-15` },
        { key: `all-saints-${year}`, nameKey: 'allSaints', date: `${year}-11-01` },
        { key: `immaculate-${year}`, nameKey: 'immaculateConception', date: `${year}-12-08` },
        { key: `christmas-${year}`, nameKey: 'christmas', date: `${year}-12-25` },
        { key: `st-stephen-${year}`, nameKey: 'stStephen', date: `${year}-12-26` }
      )
      break
    case 'pt':
      holidays.push(
        { key: `new-year-${year}`, nameKey: 'newYear', date: `${year}-01-01` },
        { key: `good-friday-${year}`, nameKey: 'goodFriday', date: goodFridayStr },
        { key: `labour-day-${year}`, nameKey: 'labourDay', date: `${year}-05-01` },
        { key: `corpus-christi-${year}`, nameKey: 'corpusChristi', date: corpusChristiStr },
        { key: `portugal-day-${year}`, nameKey: 'portugalDay', date: `${year}-06-10` },
        { key: `assumption-${year}`, nameKey: 'assumptionDay', date: `${year}-08-15` },
        { key: `republic-pt-${year}`, nameKey: 'republicDayPt', date: `${year}-10-05` },
        { key: `all-saints-${year}`, nameKey: 'allSaints', date: `${year}-11-01` },
        { key: `independence-pt-${year}`, nameKey: 'independencePt', date: `${year}-12-01` },
        { key: `immaculate-${year}`, nameKey: 'immaculateConception', date: `${year}-12-08` },
        { key: `christmas-${year}`, nameKey: 'christmas', date: `${year}-12-25` }
      )
      break
    default:
      holidays.push(
        { key: `new-year-${year}`, nameKey: 'newYear', date: `${year}-01-01` },
        { key: `easter-monday-${year}`, nameKey: 'easterMonday', date: easterMondayStr },
        { key: `labour-day-${year}`, nameKey: 'labourDay', date: `${year}-05-01` },
        { key: `christmas-${year}`, nameKey: 'christmas', date: `${year}-12-25` }
      )
  }

  return holidays
}

/**
 * Converts GBP specialHourPeriods to the UI holidays/vacationPeriods representation.
 * Populates holiday cards from matching date entries; multi-day periods become vacations.
 * @param {Array} holidays - Pre-built holiday array to mutate
 * @param {Array} periods - GBP specialHourPeriods
 * @returns {{ vacationPeriods: Array }}
 */
function initFromGbpPeriods(holidays, periods) {
  const vacationPeriods = []
  const pastSpecialHours = []
  let vacCounter = 0
  const todayStr = toDateStr(new Date())

  if (!Array.isArray(periods)) return { vacationPeriods, pastSpecialHours }

  const pad = n => String(n).padStart(2, '0')

  periods.forEach(period => {
    const { startDate, endDate, openTime, closeTime, closed, isClosed } = period
    if (!startDate) return

    const dateStr = `${startDate.year}-${pad(startDate.month)}-${pad(startDate.day)}`
    const isPast = dateStr < todayStr

    if (isPast) {
      pastSpecialHours.push({
        id: `past-${dateStr}-${Math.random()}`,
        date: dateStr,
        endDateStr: endDate ? `${endDate.year}-${pad(endDate.month)}-${pad(endDate.day)}` : null,
        isOpen: !(closed || isClosed),
        openTime: openTime || '09:00',
        closeTime: closeTime || '18:00',
        rawPeriod: period
      })
      return
    }

    const isMultiDay = endDate &&
      (endDate.year !== startDate.year || endDate.month !== startDate.month || endDate.day !== startDate.day)

    if (isMultiDay) {
      vacationPeriods.push({
        id: `vac-${++vacCounter}`,
        name: '',
        startDate: `${startDate.year}-${pad(startDate.month)}-${pad(startDate.day)}`,
        endDate: `${endDate.year}-${pad(endDate.month)}-${pad(endDate.day)}`
      })
      return
    }

    const holiday = holidays.find(h => h.date === dateStr)
    const isClosed_ = closed || isClosed

    if (holiday) {
      holiday.isExpanded = true
      holiday.isOpen = !isClosed_
      if (!isClosed_) {
        const allDay = openTime === '00:00' && (closeTime === '00:00' || closeTime === '24:00')
        holiday.isAllDay = allDay
        if (!allDay && openTime && closeTime) {
          holiday.slots = [{ from: openTime, to: closeTime }]
        }
      }
    } else {
      holidays.push({
        key: `custom-${dateStr}-${Date.now()}`,
        nameKey: null,
        name: dateStr,
        date: dateStr,
        isExpanded: true,
        isOpen: !isClosed_,
        isAllDay: !isClosed_ && openTime === '00:00' && (closeTime === '00:00' || closeTime === '24:00'),
        slots: openTime && closeTime ? [{ from: openTime, to: closeTime }] : [{ from: '09:00', to: '18:00' }],
        isCustom: true
      })
    }
  })

  holidays.sort((a, b) => a.date.localeCompare(b.date))
  return { vacationPeriods, pastSpecialHours }
}

function buildSpecialHourPeriods(holidays, vacationPeriods) {
  const periods = []

  holidays.forEach(holiday => {
    if (!holiday.isExpanded) return
    const [year, month, day] = holiday.date.split('-').map(Number)
    const startDate = { year, month, day }
    if (!holiday.isOpen) { periods.push({ startDate, closed: true }); return }
    if (holiday.isAllDay) { periods.push({ startDate, openTime: '00:00', closeTime: '00:00', closed: false }); return }
    holiday.slots.forEach(slot => {
      periods.push({ startDate, openTime: slot.from, closeTime: slot.to, closed: false })
    })
  })

  vacationPeriods.forEach(period => {
    if (!period.startDate || !period.endDate) return
    const [sy, sm, sd] = period.startDate.split('-').map(Number)
    const [ey, em, ed] = period.endDate.split('-').map(Number)
    periods.push({ startDate: { year: sy, month: sm, day: sd }, endDate: { year: ey, month: em, day: ed }, closed: true })
  })

  return periods
}

let vacCounter = 0

export default {
  name: 'tab-special-hours',

  props: {
    specialHourPeriods: {
      type: Array,
      default: () => []
    }
  },

  data() {
    const today = new Date()
    const todayStr = toDateStr(today)
    const currentYear = today.getFullYear()
    const locale = this.$i18n.locale || 'fr'

    const holidaysThisYear = getHolidaysForLocale(locale, currentYear)
    const holidaysNextYear = getHolidaysForLocale(locale, currentYear + 1)
    const allHolidays = [...holidaysThisYear, ...holidaysNextYear]

    // Only keep upcoming holidays up to exactly 1 year (12 months) from today
    const oneYearFromToday = new Date()
    oneYearFromToday.setFullYear(oneYearFromToday.getFullYear() + 1)
    const oneYearFromTodayStr = toDateStr(oneYearFromToday)

    const holidayDefs = allHolidays.filter(h => h.date >= todayStr && h.date <= oneYearFromTodayStr)

    const holidays = holidayDefs.map(def => ({
      key: def.key,
      nameKey: def.nameKey,
      name: this.$t(`dash.presenceManagement.specialHours.holidays.${def.nameKey}`),
      date: def.date,
      isExpanded: false,
      isOpen: true,
      isAllDay: false,
      slots: [{ from: '09:00', to: '18:00' }],
      isCustom: false
    }))

    const { vacationPeriods, pastSpecialHours } = initFromGbpPeriods(holidays, this.specialHourPeriods)

    return {
      currentYear,
      sectionsOpen: { holidays: true, vacations: false, archives: false },
      holidays,
      vacationPeriods,
      pastSpecialHours,
      showAddCustomDayModal: false,
      customDayForm: { name: '', date: '' }
    }
  },

  methods: {
    /** Returns current state as GBP specialHourPeriods. Called by the parent on save. */
    getSpecialHoursData() {
      return { specialHourPeriods: buildSpecialHourPeriods(this.holidays, this.vacationPeriods) }
    },

    removePastItem(idx) {
      this.pastSpecialHours.splice(idx, 1)
    },

    purgeAllArchives() {
      this.pastSpecialHours = []
    },

    renewPastItem(idx) {
      const item = this.pastSpecialHours[idx]
      if (!item) return

      const pad = n => String(n).padStart(2, '0')
      const todayStr = toDateStr(new Date())
      const [oldY, m, d] = item.date.split('-').map(Number)
      
      let targetYear = this.currentYear
      let newDateStr = `${targetYear}-${pad(m)}-${pad(d)}`
      if (newDateStr < todayStr) {
        targetYear++
        newDateStr = `${targetYear}-${pad(m)}-${pad(d)}`
      }

      const existingHoliday = this.holidays.find(h => h.date === newDateStr)
      if (existingHoliday) {
        existingHoliday.isExpanded = true
        existingHoliday.isOpen = item.isOpen
        if (item.isOpen) {
          existingHoliday.slots = [{ from: item.openTime, to: item.closeTime }]
        }
      } else {
        this.holidays.push({
          key: `custom-${newDateStr}-${Date.now()}`,
          nameKey: null,
          name: newDateStr,
          date: newDateStr,
          isExpanded: true,
          isOpen: item.isOpen,
          isAllDay: false,
          slots: [{ from: item.openTime, to: item.closeTime }],
          isCustom: true
        })
      }

      this.holidays.sort((a, b) => a.date.localeCompare(b.date))
      this.pastSpecialHours.splice(idx, 1)
    },

    renewAllArchives() {
      const itemsToRenew = [...this.pastSpecialHours]
      itemsToRenew.forEach(() => {
        this.renewPastItem(0)
      })
    },

    toggleSection(key) {
      this.$set(this.sectionsOpen, key, !this.sectionsOpen[key])
    },

    toggleHoliday(key) {
      const holiday = this.holidays.find(h => h.key === key)
      if (holiday) this.$set(holiday, 'isExpanded', !holiday.isExpanded)
    },

    onToggleHolidayOpen(key, isOpen) {
      const holiday = this.holidays.find(h => h.key === key)
      if (holiday && !isOpen) this.$set(holiday, 'isAllDay', false)
    },

    addHolidaySlot(key) {
      const holiday = this.holidays.find(h => h.key === key)
      if (!holiday || holiday.slots.length >= 4) return
      const pad = n => String(n).padStart(2, '0')
      const last = holiday.slots[holiday.slots.length - 1]
      let fromH = 14, fromM = 0
      if (last?.to) { const [h, m] = last.to.split(':').map(Number); fromH = Math.min(h + 1, 23); fromM = m }
      const toH = Math.min(fromH + 2, 23)
      holiday.slots.push({ from: `${pad(fromH)}:${pad(fromM)}`, to: `${pad(toH)}:${pad(fromM)}` })
    },

    removeHolidaySlot(key, idx) {
      const holiday = this.holidays.find(h => h.key === key)
      if (holiday) holiday.slots.splice(idx, 1)
    },

    removeCustomDay(key) {
      const idx = this.holidays.findIndex(h => h.key === key)
      if (idx !== -1) this.holidays.splice(idx, 1)
    },

    addVacationPeriod() {
      const today = toDateStr(new Date())
      this.vacationPeriods.push({ id: `vac-${++vacCounter}`, name: '', startDate: today, endDate: today })
    },

    removeVacationPeriod(idx) {
      this.vacationPeriods.splice(idx, 1)
    },

    openAddCustomDayModal() {
      this.customDayForm.name = ''
      this.customDayForm.date = ''
      this.showAddCustomDayModal = true
    },

    addCustomDay() {
      if (!this.customDayForm.date) return
      this.holidays.push({
        key: `custom-${Date.now()}`,
        nameKey: null,
        name: this.customDayForm.date,
        date: this.customDayForm.date,
        isExpanded: true,
        isOpen: true,
        isAllDay: false,
        slots: [{ from: '09:00', to: '18:00' }],
        isCustom: true
      })
      this.holidays.sort((a, b) => a.date.localeCompare(b.date))
      this.showAddCustomDayModal = false
    },

    formatDate(dateStr) {
      if (!dateStr) return ''
      const [year, month, day] = dateStr.split('-').map(Number)
      return new Date(year, month - 1, day).toLocaleDateString(this.$i18n?.locale || 'fr-FR', {
        weekday: 'long', day: '2-digit', month: 'long', year: 'numeric'
      })
    }
  }
}
</script>

<style scoped>
.bshe-subtab { width: 100%; display: flex; flex-direction: column; gap: 16px; }

.bshe-section {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(46, 25, 55, 0.07);
  overflow: hidden;
}

.bshe-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 28px;
  cursor: pointer;
  user-select: none;
  border-bottom: 1px solid #f0e8f6;
}

.bshe-section-header:hover { background: #fdf9fd; }

.bshe-section-title { margin: 0; font-size: 16px; font-weight: 700; color: #6a1f4c; }
.bshe-section-chevron { color: #6a1f4c; font-size: 22px; flex-shrink: 0; }

.bshe-section-content { padding: 24px 28px; }
.bshe-section-content--vacations { display: flex; flex-direction: column; gap: 12px; }

.bshe-cards-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  align-items: start;
}

.bshe-card {
  border: 1px solid #e8daf0;
  border-radius: 8px;
  overflow: hidden;
  transition: border-color 0.2s, box-shadow 0.2s;
  cursor: pointer;
}

.bshe-card:hover { border-color: #c8a8d8; box-shadow: 0 2px 8px rgba(106, 31, 76, 0.08); }
.bshe-card--expanded { border-color: #6a1f4c; }

.bshe-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 14px 16px;
  gap: 12px;
}

.bshe-card-header-left {
  flex: 1 1 auto;
  min-width: 0;
}

.bshe-card-header-right {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-shrink: 0;
  margin-left: auto;
}

.bshe-card-name { margin: 0 0 6px; font-size: 14px; font-weight: 700; color: #6a1f4c; }
/* .bshe-card-date { margin: 0; font-size: 12px; color: #9e8dae; text-transform: capitalize; }
 */
.bshe-card-date {
  margin: 0;
  font-size: 13px;
  color: #9e8dae;
  text-transform: capitalize;
}

.bshe-card-date--custom {
  text-transform: none;
}

.bshe-card-chevron {
  color: #c0afd0;
  font-size: 20px;
  transition: color 0.2s;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.bshe-card--expanded .bshe-card-chevron { color: #6a1f4c; }

button.bshe-card-del-btn {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 26px !important;
  height: 26px !important;
  min-width: 26px !important;
  max-width: 26px !important;
  padding: 0 !important;
  margin: 0 !important;
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
  color: #8c7b9e !important;
  cursor: pointer !important;
  flex-shrink: 0 !important;
  transition: color 0.18s;
}
button.bshe-card-del-btn:hover {
  color: #6a1f4c !important;
}

button.bshe-card-del-btn i,
button.bshe-card-del-btn .material-icons {
  font-size: 16px !important;
  line-height: 1 !important;
  color: #d4c1e3;
}

button.bshe-card-del-btn i:hover {
  color: #6a1f4c !important;
}

.bshe-card-editor {
  padding: 16px;
  border-top: 1px solid #f0e8f6;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* ─── Add custom day card ─── */
.bshe-card--add {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 12px 16px;
  border: 1px dashed #e8daf0;
  gap: 5px;
  cursor: pointer;
}

.bshe-card--add:hover {
  border-color: #6a1f4c;
  background: #fdf9fd;
}

.bshe-card-add-label {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: #6a1f4c;
  text-align: center;
}

.bshe-card-add-icon {
  font-size: 18px;
  color: #6a1f4c;
}

.bshe-card-add-label { margin: 0; font-size: 13px; font-weight: 600; color: #6a1f4c; text-align: center; }
.bshe-card-add-icon { font-size: 18px; color: #6a1f4c; }

.bshe-toggle-group { display: flex; align-items: center; gap: 10px; }
.bshe-toggle-label { font-size: 13px; font-weight: 500; color: #3d2a4a; white-space: nowrap; }
.bshe-toggle-label--secondary { color: #9e8dae; }

::v-deep .bshe-toggle-group .vs-switch {
  background-color: #d8cce2 !important;
  box-shadow: none !important;
  -webkit-box-shadow: none !important;
  border: none !important;
  min-width: 44px !important; width: 44px !important; height: 22px !important;
  border-radius: 11px !important; padding: 2px !important;
  flex-shrink: 0; position: relative !important;
}
::v-deep .bshe-toggle-group .vs-switch.vs-switch-active { background-color: #6a1f4c !important; }
::v-deep .bshe-toggle-group .vs-switch[disabled] { background-color: #ece5f3 !important; opacity: 0.7 !important; cursor: not-allowed !important; }

.bshe-slot-row { display: flex; align-items: center; gap: 8px; }

.bshe-time-input {
  border: 1px solid #e2d5ef; border-radius: 6px; padding: 6px 10px;
  font-size: 13px; color: #3d2a4a; background: #fff; width: 90px;
  outline: none; transition: border-color 0.2s;
}
.bshe-time-input:focus { border-color: #6a1f4c; }
.bshe-time-input::-webkit-calendar-picker-indicator { display: none; }
.bshe-time-sep { font-size: 13px; color: #9e8dae; }

.bshe-slot-actions { display: flex; align-items: center; gap: 2px; min-width: 26px; }

button.bshe-icon-btn {
  background: transparent !important; background-color: transparent !important;
  box-shadow: none !important; -webkit-box-shadow: none !important;
  border: none !important; padding: 4px !important; border-radius: 4px !important;
  min-width: 0 !important; width: auto !important;
  display: inline-flex !important; align-items: center !important;
  transition: color 0.18s; cursor: pointer !important;
}
button.bshe-icon-btn i { font-size: 18px; }
button.bshe-icon-btn--del { color: #d4c1e3 !important; }
button.bshe-icon-btn--del:hover { color: #6a1f4c !important; }
button.bshe-icon-btn--add { color: #d4c1e3 !important; }
button.bshe-icon-btn--add:hover { color: #6a1f4c !important; }

.bshe-vacations-hint {
  margin: 0;
  font-size: 13px;
  color: #524361;
  background: #faf5fd;
  border-left: 3px solid #c8a8d8;
  padding: 10px 14px;
  border-radius: 0 6px 6px 0;
}

.bshe-vacation-row { 
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
 }

.bshe-text-input {
  border: 1px solid #e2d5ef;
  border-radius: 6px;
  padding: 7px 12px;
  font-size: 13px;
  color: #3d2a4a;
  background: #fff;
  outline: none;
  transition: border-color 0.2s;
  min-width: 160px;
}
.bshe-text-input:focus { border-color: #6a1f4c; }

.bshe-date-input {
  border: 1px solid #e2d5ef; border-radius: 6px; padding: 6px 10px;
  font-size: 13px; color: #3d2a4a; background: #fff; outline: none;
  cursor: pointer; transition: border-color 0.2s;
}
.bshe-date-input:focus { border-color: #6a1f4c; }
.bshe-vac-sep { font-size: 13px; color: #9e8dae; white-space: nowrap; }

button.bshe-add-vacation-btn,
button.bshe-add-vacation-btn span {
  background: transparent !important; background-color: transparent !important;
  box-shadow: none !important; -webkit-box-shadow: none !important;
  border: none !important; color: #6a1f4c !important;
  font-size: 13px !important; font-weight: 500 !important; padding: 6px 0 !important;
  min-width: 0 !important; width: auto !important;
  display: inline-flex !important; align-items: center !important;
  gap: 6px !important; cursor: pointer !important; align-self: flex-start;
  text-transform: none !important;
  line-height: 1 !important;
}

button.bshe-add-vacation-btn i {
  transform: translateY(1.4px);
  font-size: 16px !important;
}

button.bshe-add-vacation-btn:hover { color: #6a1f4c !important; background: transparent !important; box-shadow: none !important; }

.bshe-modal-overlay {
  position: fixed; inset: 0; 
  background: rgba(153, 148, 154, 0.45);
  display: flex; align-items: center; justify-content: center; z-index: 9999;
}
.bshe-modal {
  background: #fff; border-radius: 12px; padding: 28px 32px;
  width: 100%; max-width: 420px;
  box-shadow: 0 8px 32px rgba(46, 25, 55, 0.18);
  display: flex; flex-direction: column; gap: 18px;
}
.bshe-modal-title { margin: 0; font-size: 17px; font-weight: 700; color: #6a1f4c; }
.bshe-modal-field { display: flex; flex-direction: column; gap: 6px; }
.bshe-modal-label { font-size: 13px; font-weight: 500; color: #3d2a4a; }
.bshe-modal-field .bshe-text-input, .bshe-modal-field .bshe-date-input { width: 100%; box-sizing: border-box; }
.bshe-modal-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 4px; }

/* ─── Archives Styling ─── */
.bshe-archives-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.bshe-archives-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.bshe-btn-archive-action {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;
  border: 1px solid transparent;
}

.bshe-btn-archive-action i {
  font-size: 16px;
}

.bshe-btn-archive-action ::v-deep span {
  text-transform: none !important;
}

.bshe-btn-archive-action--renew {
  background: #f3edf7;
  color: #6a1f4c;
}

.bshe-btn-archive-action--renew:hover {
  background: #6a1f4c;
  color: #fff;
}

.bshe-btn-archive-action--purge {
  background: #fdf2f2;
  color: #d32f2f;
}

.bshe-btn-archive-action--purge:hover {
  background: #d32f2f;
  color: #fff;
}

.bshe-archives-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.bshe-archive-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #faf8fc;
  border: 1px solid #eee6f3;
  border-radius: 8px;
  padding: 12px 16px;
  gap: 16px;
}

.bshe-archive-info {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.bshe-archive-date {
  font-size: 13.5px;
  font-weight: 700;
  color: #3d2a4a;
}

.bshe-archive-status {
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 12px;
}

.bshe-archive-status--open {
  background: #e8f5e9;
  color: #2e7d32;
}

.bshe-archive-status--closed {
  background: #ffebee;
  color: #c62828;
}

.bshe-archive-item-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.bshe-btn-renew-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #6a1f4c;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  padding: 5px 12px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s;
}

.bshe-btn-renew-item i {
  font-size: 14px;
}

@media (max-width: 1199px) { .bshe-cards-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 767px) { .bshe-cards-grid { grid-template-columns: 1fr; } }
</style>
